<!-- Floating Chat Button -->
@if (!isChatOpen) {
  <div
    class="floating-chat-button"
    [class.has-new-messages]="hasNewMessages"
    (click)="toggleChat()"
    (keydown.enter)="toggleChat()"
    (keydown.space)="toggleChat()"
    tabindex="0"
    role="button"
    aria-label="Open chat with ScanNinja AI"
  >
    <img
      src="assets/images/scan-ninja-plan-icon-black.png"
      alt="ScanNinja AI"
      class="chatbot-icon"
      width="50"
      height="50"
    />
    @if (hasNewMessages) {
      <div class="notification-badge"></div>
    }
  </div>
}

<!-- Chat Window -->
@if (isChatOpen) {
  <div
    class="chat-window"
    [class.open]="isChatOpen"
    [class.minimized]="isMinimized"
    [class.expanded]="isExpanded"
  >
    <!-- Backdrop for expanded mode -->
    @if (showBackdrop) {
      <div
        class="chat-backdrop"
        (click)="toggleExpand()"
        aria-hidden="true"
      ></div>
    }
    <!-- Chat Header -->
    <div class="chat-header">
      <div class="chat-title">
        <img
          src="assets/images/scan-ninja-plan-icon-black.png"
          alt="ScanNinja AI"
          class="me-2 chatbot-icon"
          width="20"
          height="20"
        />
        <span>ScanNinja AI</span>
        <span class="online-indicator"></span>
      </div>
      <!-- Ninja Coins balance chip -->
      @if (creditSummary && creditSummary.monthlyAllotment > 0) {
        <a
          class="credits-chip"
          [routerLink]="['/dashboard/company/billing']"
          (click)="$event.stopPropagation()"
          [title]="ninjaCreditsService.formatCredits(creditSummary.totalAvailable) + ' Coins remaining'"
          aria-label="View Coins balance"
        >
          <i class="fas fa-coins" aria-hidden="true"></i>
          <span>{{ ninjaCreditsService.formatCredits(creditSummary.totalAvailable) }}</span>
        </a>
      }
      <div class="chat-actions">
        <button
          class="chat-action-btn btn-expand"
          (click)="toggleExpand()"
          [title]="isExpanded ? 'Restore size' : 'Expand'"
          [aria-label]="isExpanded ? 'Restore chat size' : 'Expand chat'"
          type="button"
        >
          <i
            class="fas"
            [ngClass]="
              isExpanded
                ? 'fa-compress'
                : 'fa-up-right-and-down-left-from-center'
            "
          ></i>
          <span class="visually-hidden">{{
            isExpanded ? "Restore chat size" : "Expand chat"
          }}</span>
        </button>
        @if (!isMinimized) {
          <button
            class="chat-action-btn btn-minimize"
            (click)="minimizeChat()"
            title="Minimize"
            aria-label="Minimize chat"
            type="button"
          >
            <i class="fas fa-minus"></i>
          </button>
        }
        @if (isMinimized) {
          <button
            class="chat-action-btn btn-maximize"
            (click)="maximizeChat()"
            title="Maximize"
            aria-label="Maximize chat"
            type="button"
          >
            <i class="fas fa-expand"></i>
          </button>
        }
        <button
          class="chat-action-btn btn-close"
          (click)="toggleChat()"
          title="Close"
          aria-label="Close chat"
          type="button"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    <!-- Chat Content (only show when not minimized) -->
    @if (!isMinimized) {
      <div class="chat-content">
        <!-- Messages Container -->
        <div class="chat-messages" #chatMessages>
          <!-- Welcome Message / Example Queries -->
          @if (messages.length <= 1) {
            <div class="example-queries">
              <div class="example-title">
                <i class="fas fa-lightbulb me-2"></i>
                Try asking me:
              </div>
              <div class="example-list">
                @for (example of exampleQueries; track example) {
                  <button
                    class="bg-body example-query-btn"
                    (click)="sendExampleQuery(example)"
                    [disabled]="isStreaming"
                  >
                    {{ example }}
                  </button>
                }
              </div>
            </div>
          }
          <!-- Chat Messages -->
          @for (message of messages; track message.id) {
            <div [class]="getMessageClasses(message)">
              @if (message.type === "bot") {
                <div class="message-avatar">
                  <img
                    src="assets/images/scan-ninja-plan-icon-black.png"
                    alt="ScanNinja AI"
                    class="chatbot-icon"
                    width="24"
                    height="24"
                  />
                </div>
              }
              <div class="message-content">
                <div
                  class="message-text text-html"
                  [innerHTML]="getMessageHtml(message)"
                ></div>
                <!-- Typing Indicator -->
                @if (message.isStreaming) {
                  <div class="typing-indicator">
                    <div class="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <span class="typing-text"
                      >Analyzing your security data...</span
                    >
                  </div>
                }
                <!-- Message Metadata -->
                <div class="message-meta">
                  <span class="message-time">{{
                    message.timestamp | date: "short"
                  }}</span>
                  @if (hasContextInfo(message)) {
                    <span class="context-info">
                      <i class="fas fa-database me-1"></i>
                      {{ getContextInfo(message) }}
                    </span>
                  }
                  @if (ragContext && !message.isStreaming && message.type === 'bot' && message.id === messages[messages.length - 1]?.id) {
                    @if (getRagSummary(); as summary) {
                      <span class="context-info">
                        <i class="fas fa-info-circle me-1"></i>
                        <span>
                          <strong>Context:</strong> {{ summary.scans }} scans, {{ summary.vulns }} vulnerabilities
                          @if (summary.assets !== undefined) {
                            <span> · {{ summary.assets }} assets</span>
                          }
                          @if (summary.genAt) {
                            <span> · as of {{ summary.genAt }}</span>
                          }
                        </span>
                      </span>
                    }
                  }
                </div>
              </div>
              @if (message.type === "user") {
                <div class="message-avatar">
                  <i class="fas fa-user"></i>
                </div>
              }
            </div>
          }
        </div>
        <!-- Chat Input -->
        <div class="chat-input-container">
          @if (messages.length > 1) {
            <div class="chat-actions-bar">
              <button
                class="btn-clear-chat"
                (click)="clearChat()"
                [disabled]="isStreaming"
                title="Clear Chat History"
              >
                <i class="fas fa-trash me-1"></i>
                Clear
              </button>
              <button
                class="btn-restart-chat"
                (click)="restartConversation()"
                [disabled]="isStreaming"
                title="Restart Conversation"
              >
                <i class="fas fa-redo me-1"></i>
                Restart
              </button>
              @if (isStreaming) {
                <button
                  class="btn-stop-chat"
                  (click)="stopStreaming()"
                  title="Stop Response"
                >
                  <i class="fas fa-stop me-1"></i>
                  Stop
                </button>
              }
            </div>
          }
          <div class="chat-input">
            <!-- Low-credit warning -->
            @if (creditSummary && creditSummary.totalAvailable < creditSummary.monthlyAllotment * 0.2 && creditSummary.totalAvailable > 0) {
              <div class="chat-credit-warning">
                <i class="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>
                Low Coins (<strong>{{ ninjaCreditsService.formatCredits(creditSummary.totalAvailable) }}</strong> left).
                <a [routerLink]="['/dashboard/company/billing']" (click)="toggleChat()">Buy more</a>
              </div>
            }
            @if (creditSummary && creditSummary.totalAvailable <= 0) {
              <div class="chat-credit-warning chat-credit-exhausted">
                <i class="fas fa-ban me-1" aria-hidden="true"></i>
                No Coins remaining.
                <a [routerLink]="['/dashboard/company/billing']" (click)="toggleChat()">Purchase coins</a>
              </div>
            }
            <textarea title="Form text area"
              #queryInput
              [(ngModel)]="currentQuery"
              (keydown)="onKeyDown($event)"
              placeholder="Ask about your scans, vulnerabilities, remediation, or how to use the platform..."
              [disabled]="isStreaming"
              rows="1"
            ></textarea>
            <button
              title="Send message"
              class="send-btn"
              (click)="sendMessage()"
              [disabled]="!currentQuery.trim() || isStreaming"
              [title]="isStreaming ? 'Sending...' : 'Send message'"
            >
              @if (!isStreaming) {
                <i class="fas fa-paper-plane"></i>
              }
              @if (isStreaming) {
                <i class="fas fa-spinner fa-spin"></i>
              }
            </button>
          </div>
          <!-- Cost hint -->
          <div class="chat-credit-hint">
            <i class="fas fa-coins me-1" aria-hidden="true"></i>
            {{ aiQueryCost }} coins per query
          </div>
        </div>
      </div>
    }
    <!-- Minimized Status -->
    @if (isMinimized) {
      <div class="minimized-status">
        <div class="status-text">
          <img
            src="assets/images/scan-ninja-plan-icon-black.png"
            alt="ScanNinja AI"
            class="me-2 chatbot-icon"
            width="20"
            height="20"
          />
          ScanNinja AI is ready
        </div>
        @if (isStreaming) {
          <div class="pulse-indicator"></div>
        }
      </div>
    }
  </div>
}
